import { apiFetch, apiFetchRaw } from "./api";

// ---------- Types mirroring backend responses ----------

export type BackendPlanTier = "BASIC" | "PRO" | "PREMIUM" | "ENTERPRISE";
export type BackendTenantStatus = "ACTIVE" | "SUSPENDED" | "TRIAL" | "PENDING";
export type BackendUserStatus = "ACTIVE" | "DISABLED" | "PENDING";
export type BackendUserRole =
  | "SUPER_ADMIN"
  | "TENANT_OWNER"
  | "HR_MANAGER"
  | "SITE_ADMIN"
  | "OPERATIONS_ADMIN"
  | "SUPERVISOR"
  | "EMPLOYEE";

export interface Paginated<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface PlatformTenant {
  id: string;
  code: string;
  name: string;
  industry: string | null;
  region: string | null;
  status: BackendTenantStatus;
  plan: { id: string; tier: BackendPlanTier; name: string };
  deploymentMode: string;
  supportTier: string;
  primaryContact: string | null;
  contactEmail: string | null;
  renewalDate: string | null;
  createdAt: string;
  counts: {
    activeUsers: number;
    employees: number;
    sites: number;
    attendanceToday: number;
    pendingRequests: number;
  };
  /**
   * Licence utilisation (FEAT-2026-018). `used` counts employee records
   * regardless of status; `limit`/`percentage` are null when uncapped.
   * The platform operator's view of the contractual cap — the 90% notice
   * itself goes to the tenant's own admins, since notifications are
   * tenant-scoped.
   */
  licence: { used: number; limit: number | null; percentage: number | null };
}

export interface PlatformTenantDetail extends PlatformTenant {
  settings: Record<string, unknown> | null;
}

export interface CreateTenantResponse extends PlatformTenant {
  /**
   * Always `null` post-Clerk-migration. Tenant creation no longer
   * provisions an owner inline — Clerk operations can't transactionally
   * span DB writes, so the owner is created via a follow-up call to the
   * Users page after the tenant exists.
   */
  owner: null;
}

export interface PlatformUser {
  id: string;
  tenantId: string | null;
  tenantName: string | null;
  name: string;
  email: string;
  /** Legacy local handle, no longer set. Sign-in is by email only (ADR-0017). */
  username: string | null;
  phone: string | null;
  department: string | null;
  roleType: BackendUserRole;
  roleLabel: string | null;
  status: BackendUserStatus;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface RoleMetadata {
  key: BackendUserRole;
  label: string;
  scope: "platform" | "tenant";
  description: string;
  assignableByTenantOwner: boolean;
}

export interface Plan {
  id: string;
  tier: BackendPlanTier;
  name: string;
  maxUsers: number;
  maxSites: number;
  pricePerMonth: string;
  features: string[];
  targetAudience: string | null;
  highlight: boolean;
}

// ---------- Requests ----------

export interface ListTenantsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  plan?: BackendPlanTier;
  status?: BackendTenantStatus;
}

export interface CreateTenantBody {
  name: string;
  code: string;
  industry?: string;
  region?: string;
  plan: BackendPlanTier;
  status?: BackendTenantStatus;
  deploymentMode?: "CLOUD" | "HYBRID" | "ON_PREM";
  supportTier?: "STANDARD" | "PRIORITY" | "DEDICATED";
  primaryContact?: string;
  contactEmail?: string;
  renewalDate?: string;
  maxUsers?: number;
  /**
   * Optional contact info for the prospective tenant owner. Used only to
   * pre-fill the tenant's `primaryContact` / `contactEmail` columns —
   * the actual admin user is created separately on the Users page after
   * the tenant exists. The `password` field that used to live here was
   * removed; Clerk handles credentials.
   */
  owner?: {
    name: string;
    email: string;
    phone?: string;
  };
}

export interface UpdateTenantBody {
  name?: string;
  industry?: string;
  region?: string;
  status?: BackendTenantStatus;
  deploymentMode?: "CLOUD" | "HYBRID" | "ON_PREM";
  supportTier?: "STANDARD" | "PRIORITY" | "DEDICATED";
  primaryContact?: string;
  contactEmail?: string;
  renewalDate?: string;
}

export interface ListPlatformUsersQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  tenantId?: string;
  status?: BackendUserStatus;
  roleType?: BackendUserRole;
}

export interface CreatePlatformUserBody {
  tenantId?: string;
  name: string;
  email: string;
  phone?: string;
  /** Free-text label — platform-level users only (BR-073). */
  department?: string;
  /** Managed department id — tenant users only (ADR-0008). */
  departmentId?: string;
  roleType: BackendUserRole;
  roleLabel?: string;
  status?: BackendUserStatus;
}

export interface UpdatePlatformUserBody {
  name?: string;
  phone?: string;
  department?: string;
  roleType?: BackendUserRole;
  roleLabel?: string;
  status?: BackendUserStatus;
}

// ---------- Helpers ----------

function toQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// ---------- Tenants ----------

export const platformTenantsApi = {
  list: (q: ListTenantsQuery = {}) =>
    apiFetch<Paginated<PlatformTenant>>(`/platform/tenants${toQuery(q as Record<string, unknown>)}`),

  get: (id: string) => apiFetch<PlatformTenantDetail>(`/platform/tenants/${id}`),

  create: (body: CreateTenantBody) =>
    apiFetch<CreateTenantResponse>(`/platform/tenants`, { method: "POST", body }),

  update: (id: string, body: UpdateTenantBody) =>
    apiFetch<PlatformTenant>(`/platform/tenants/${id}`, { method: "PATCH", body }),

  suspend: (id: string) =>
    apiFetch<PlatformTenant>(`/platform/tenants/${id}/suspend`, { method: "PATCH" }),

  reactivate: (id: string) =>
    apiFetch<PlatformTenant>(`/platform/tenants/${id}/reactivate`, { method: "PATCH" }),

  /**
   * Full tenant data export (FEAT-2026-019). Super-admin only and run on the
   * customer's request — tenants have no self-serve export.
   *
   * The response is a stream, not JSON, so it bypasses `apiFetch` and is read
   * as a blob. Large tenants produce large files; the browser writes it to disk
   * once the stream completes.
   */
  exportData: async (id: string, tenantCode: string): Promise<void> => {
    const res = await apiFetchRaw(`/platform/tenants/${id}/export`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tenant-export_${tenantCode}_${new Date().toISOString().slice(0, 10)}.ndjson`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  changePlan: (id: string, plan: BackendPlanTier) =>
    apiFetch<PlatformTenant>(`/platform/tenants/${id}/plan`, {
      method: "PATCH",
      body: { plan },
    }),

  remove: (id: string) =>
    apiFetch<{ id: string; deleted: true }>(`/platform/tenants/${id}`, { method: "DELETE" }),
};

// ---------- Bulk import ----------

export interface BulkImportRow {
  name: string;
  email: string;
  phone?: string;
  department?: string;
  roleType: BackendUserRole;
  roleLabel?: string;
}

export interface BulkImportResult {
  /** Each created user receives a Clerk invitation email; no temp password is returned. */
  created: Array<{ email: string; name: string }>;
  skipped: Array<{ email: string; reason: string }>;
  totalProcessed: number;
}

// ---------- Platform Users ----------

export const platformUsersApi = {
  list: (q: ListPlatformUsersQuery = {}) =>
    apiFetch<Paginated<PlatformUser>>(`/platform/users${toQuery(q as Record<string, unknown>)}`),

  get: (id: string) => apiFetch<PlatformUser>(`/platform/users/${id}`),

  create: (body: CreatePlatformUserBody) =>
    // See tenantUsersApi.create for the three result shapes.
    apiFetch<PlatformUser & { inviteUrl: string | null; linkedToExisting: boolean }>(
      `/platform/users`,
      { method: "POST", body },
    ),

  update: (id: string, body: UpdatePlatformUserBody) =>
    apiFetch<PlatformUser>(`/platform/users/${id}`, { method: "PATCH", body }),

  disable: (id: string) =>
    apiFetch<PlatformUser>(`/platform/users/${id}/disable`, { method: "PATCH" }),

  enable: (id: string) =>
    apiFetch<PlatformUser>(`/platform/users/${id}/enable`, { method: "PATCH" }),

  bulkImport: (tenantId: string, rows: BulkImportRow[]) =>
    apiFetch<BulkImportResult>(`/platform/users/import`, {
      method: "POST",
      body: { tenantId, rows },
    }),
};

// ---------- Roles & Plans ----------

export const rolesApi = {
  list: () => apiFetch<RoleMetadata[]>(`/platform/roles`),
};

export interface UpdatePlanBody {
  name?: string;
  maxUsers?: number;
  maxSites?: number;
  pricePerMonth?: number;
  features?: string[];
  targetAudience?: string;
  highlight?: boolean;
}

export const plansApi = {
  list: () => apiFetch<Plan[]>(`/platform/plans`),
  update: (id: string, body: UpdatePlanBody) =>
    apiFetch<Plan>(`/platform/plans/${id}`, { method: "PATCH", body }),
};

// ---------- Audit logs (FEAT-2026-010) ----------
export interface AuditLogRow {
  id: string;
  tenantId: string | null;
  tenantName: string | null;
  actorName: string;
  actorRole: string | null;
  actionType: string;
  module: string;
  target: string | null;
  /** SUCCESS | FAILURE | WARNING */
  result: string;
  /** INFO | WARNING | CRITICAL */
  severity: string;
  ipAddress: string | null;
  createdAt: string;
}
export interface AuditQuery {
  page?: number;
  pageSize?: number;
  tenantId?: string;
  module?: string;
  actionType?: string;
  severity?: string;
  search?: string;
  from?: string;
  to?: string;
}
export const auditApi = {
  platformList: (q: AuditQuery = {}) =>
    apiFetch<Paginated<AuditLogRow>>(`/audit-logs/platform${toQuery(q as Record<string, unknown>)}`),
};

// ---------- Platform sites overview (FEAT-2026-010) ----------
export interface PlatformSiteRow {
  id: string;
  name: string;
  code: string;
  tenantId: string;
  tenantName: string | null;
  region: string | null;
  status: "ACTIVE" | "INACTIVE";
  /** `null` = the site inherits its tenant's default. */
  geofenceRadiusM: number | null;
  employees: number;
  attendanceToday: number;
  alertsToday: number;
}
export interface PlatformSitesResponse extends Paginated<PlatformSiteRow> {
  summary: { total: number; active: number; inactive: number };
}
export interface PlatformSitesQuery {
  page?: number;
  pageSize?: number;
  tenantId?: string;
  status?: "ACTIVE" | "INACTIVE";
  search?: string;
}
export const platformSitesApi = {
  overview: (q: PlatformSitesQuery = {}) =>
    apiFetch<PlatformSitesResponse>(`/platform/sites${toQuery(q as Record<string, unknown>)}`),
};

// ---------- Platform broadcasts (FEAT-2026-010) ----------
export type BroadcastScope = "ALL_TENANTS" | "SELECTED_TENANTS" | "PLAN_TARGETED" | "INTERNAL";
export type BroadcastStatus = "DRAFT" | "SCHEDULED" | "SENT";
export type BroadcastSeverity = "INFO" | "WARNING" | "CRITICAL";
export interface PlatformBroadcast {
  id: string;
  title: string;
  body: string;
  scope: BroadcastScope;
  severity: BroadcastSeverity;
  status: BroadcastStatus;
  targetTenantIds: string[];
  targetPlan: BackendPlanTier | null;
  scheduledAt: string | null;
  sentAt: string | null;
  deliveredCount: number;
  createdAt: string;
}
export interface CreateBroadcastBody {
  title: string;
  body: string;
  scope: BroadcastScope;
  severity?: BroadcastSeverity;
  targetTenantIds?: string[];
  targetPlan?: BackendPlanTier;
  scheduledAt?: string;
}
export const platformBroadcastsApi = {
  list: (q: { page?: number; pageSize?: number } = {}) =>
    apiFetch<Paginated<PlatformBroadcast>>(
      `/notifications/platform/list${toQuery(q as Record<string, unknown>)}`,
    ),
  create: (body: CreateBroadcastBody) =>
    apiFetch<PlatformBroadcast>(`/notifications/platform/broadcast`, { method: "POST", body }),
};

// ---------- Platform settings (FEAT-2026-010) ----------
export interface PlatformSettings {
  maintenanceMode: boolean;
  allowNewTenantSignups: boolean;
  aiServiceEnabled: boolean;
  notificationServiceEnabled: boolean;
  integrationsEnabled: boolean;
  customization: Record<string, unknown> | null;
  updatedAt?: string;
}
export type UpdatePlatformSettingsBody = Partial<Omit<PlatformSettings, "updatedAt">>;
export const platformSettingsApi = {
  get: () => apiFetch<PlatformSettings>(`/settings/platform`),
  update: (body: UpdatePlatformSettingsBody) =>
    apiFetch<PlatformSettings>(`/settings/platform`, { method: "PATCH", body }),
};

// ---------- Dashboard ----------

export interface DashboardKpis {
  totalTenants: number;
  activeUsers: number;
  totalEmployees: number;
  mobileActiveUsers: number;
  attendanceToday: number;
  pendingRequests: number;
  activeSites: number;
  /** All sites regardless of status (FEAT-2026-006). */
  totalSites: number;
  renewalsDueThisMonth: number;
  aiInteractionsToday: number;
  platformAlerts: number;
}

export interface TenantGrowthPoint { month: string; year: number; tenants: number; added: number }
export interface PlanDistributionPoint { plan: BackendPlanTier; label: string; value: number }
export interface AttendanceVolumePoint { day: string; date: string; records: number }
export interface RequestsByStatusPoint { status: string; label: string; count: number }
/** 30-day series merged in from the retired Usage & Analytics page. */
export interface DailyActiveUsersPoint { date: string; day: string; users: number }
export interface GeofenceAlertPoint { date: string; day: string; alerts: number }
export interface RequestVolumePoint { date: string; day: string; requests: number }
export interface TopTenantEntry {
  id: string; name: string; code: string; industry: string | null;
  plan: BackendPlanTier; activeUsers: number; attendanceToday: number;
}
export interface PlatformAlert {
  id: string; severity: "INFO" | "WARNING" | "CRITICAL";
  message: string; actor: string | null; tenantName: string | null; createdAt: string;
}

export interface DashboardOverview {
  kpis: DashboardKpis;
  tenantGrowth: TenantGrowthPoint[];
  planDistribution: PlanDistributionPoint[];
  attendanceVolume: AttendanceVolumePoint[];
  requestsByStatus: RequestsByStatusPoint[];
  dailyActiveUsers: DailyActiveUsersPoint[];
  geofenceAlerts: GeofenceAlertPoint[];
  requestVolume: RequestVolumePoint[];
  topTenants: TopTenantEntry[];
  recentAlerts: PlatformAlert[];
}

export const dashboardApi = {
  overview: () => apiFetch<DashboardOverview>(`/platform/dashboard`),
};

// ---------- Display helpers ----------

export function tierToLabel(tier: BackendPlanTier): "Basic" | "Pro" | "Premium" | "Enterprise" {
  switch (tier) {
    case "BASIC": return "Basic";
    case "PRO": return "Pro";
    case "PREMIUM": return "Premium";
    case "ENTERPRISE": return "Enterprise";
  }
}

export function statusToLabel(status: BackendTenantStatus): "Active" | "Suspended" | "Trial" | "Pending" {
  switch (status) {
    case "ACTIVE": return "Active";
    case "SUSPENDED": return "Suspended";
    case "TRIAL": return "Trial";
    case "PENDING": return "Pending";
  }
}

export function userStatusToLabel(status: BackendUserStatus): "Active" | "Disabled" | "Pending" {
  switch (status) {
    case "ACTIVE": return "Active";
    case "DISABLED": return "Disabled";
    case "PENDING": return "Pending";
  }
}

export function labelToTier(label: string): BackendPlanTier {
  return label.toUpperCase() as BackendPlanTier;
}

export function labelToStatus(label: string): BackendTenantStatus {
  return label.toUpperCase() as BackendTenantStatus;
}
